import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode.react';
import { FiCopy, FiCheck } from 'react-icons/fi';
import PlatformIcon from './PlatformIcon';
import PaymentNoticeModal from './PaymentNoticeModal';
import { buildRaastP2PPayload } from '../utils/raastQr';
import { bankFromIban } from '../utils/pakistaniBanks';
import './PaymentListItem.css';

// A payment entry is not a link, so it cannot be an <a>. Everything above
// it in the list opens somewhere; this one reveals a value to copy --
// tap the row, see the value, tap Copy. Rendered as a <button> rather
// than a styled <div> so it is focusable and announced as pressable
// without reinventing keyboard handling.
//
// Only the IBAN gets a QR. A Raast P2P QR is IBAN-based, so there is
// nothing to encode for a Raast ID or a wallet number -- and a QR that
// silently encodes the wrong thing is worse than no QR, because it fails
// at the till rather than in testing.

// navigator.clipboard needs a secure context and is missing on some older
// mobile browsers. The textarea fallback is the pre-Clipboard-API method,
// kept because the whole feature is "let someone copy this" and a browser
// without the modern API is exactly the case that must still work.
const copyText = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (err) {
    // Fall through -- a rejected permission is not a reason to give up.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Off-screen rather than hidden: display:none and visibility:hidden
    // both make the selection un-copyable in some browsers.
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.setAttribute('readonly', '');
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    return false;
  }
};

// Whether this visitor has already seen the verification notice for this
// profile, in this browsing session.
//
// sessionStorage rather than localStorage: a visitor is anonymous, so
// there is no account to hang the flag on the way the owner's beta notice
// hangs on paymentNoticeAckedAt. localStorage would suppress a safety
// notice on that device permanently, including for someone returning
// months later to pay a different person; sessionStorage lets it come
// back with a new session. Plain component state would not survive a
// reload, which in practice is close to showing it on every tap.
//
// Keyed by profile, not global: paying a different person is a new
// decision and deserves the notice again, while tapping a second method
// on the same profile does not.
//
// Wrapped in try/catch because sessionStorage throws rather than no-ops
// in Safari private mode and wherever storage is disabled. Failing to
// read is treated as "not seen", so the notice shows -- the safe
// direction -- and failing to write simply means it shows again.
const NOTICE_SEEN_PREFIX = 'dcc:payment-notice-seen:';

const hasSeenNotice = (profileKey) => {
  try {
    return window.sessionStorage.getItem(NOTICE_SEEN_PREFIX + profileKey) === '1';
  } catch (err) {
    return false;
  }
};

const rememberNoticeSeen = (profileKey) => {
  try {
    window.sessionStorage.setItem(NOTICE_SEEN_PREFIX + profileKey, '1');
  } catch (err) {
    // Nothing to do -- the notice will simply be shown again.
  }
};

export default function PaymentListItem({
  entry,
  displayName,
  subtitle,
  handleClicks,
  isPublic,
  profileKey,
  isOpen,
  onToggle,
  size,
}) {
  // Open/closed is owned by the list, not by this row: only the parent
  // can close a sibling, which is what makes this an accordion (see
  // usePaymentAccordion). Everything below stays local -- it describes
  // this row's own copy button and its own notice, and means nothing to
  // any other row.
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [noticeOpen, setNoticeOpen] = useState(false);

  // Clear the copy feedback whenever the row closes -- including when it
  // is closed by another row being opened, which the row itself does not
  // initiate. Without this, reopening within the two-second window shows
  // a "Copied" that refers to a copy the visitor may not have made on
  // this visit to the row.
  useEffect(() => {
    if (!isOpen) {
      setCopied(false);
      setCopyFailed(false);
    }
  }, [isOpen]);

  const isIban = entry.platform === 'iban';

  // Built at render, not stored: the payload is a pure function of the
  // IBAN, so persisting it would only create a second copy that can go
  // stale. Throws on an invalid IBAN -- which the server already rejects
  // at save time -- so a legacy row that somehow predates that
  // validation degrades to "no QR" instead of taking the profile down.
  let qrPayload = null;
  if (isIban) {
    try {
      qrPayload = buildRaastP2PPayload(entry.value);
    } catch (err) {
      qrPayload = null;
    }
  }

  // Read out of the IBAN rather than stored alongside it, for the same
  // reason the QR payload is: it is a pure function of the value, so a
  // stored copy could only ever go stale or disagree. Deriving it is
  // also what makes a single "IBAN" entry type safe -- there is no way
  // for the bank shown to be a different bank from the one the account
  // belongs to. null for an unrecognised or unlisted bank code, which
  // renders exactly as an IBAN entry did before this: generic icon, no
  // bank name.
  const bank = isIban ? bankFromIban(entry.value) : null;

  // The bank name is rendered inline after the label rather than on a
  // line of its own -- see the markup below. It used to take the second
  // line, which made an IBAN the only three-line row in a list where
  // everything else is a name and a count.
  //
  // The subtitle keeps its own line, but is now only reached when
  // computeSocialDisplay actually set one, which it does only to tell
  // two otherwise-identical entries apart. That is the one case worth a
  // third line: two unlabelled IBANs at the same bank are
  // indistinguishable without their values.

  // Reveals the value, and records the tap. Recorded on reveal only, not
  // on collapse -- otherwise one interest in an IBAN counts as two taps.
  const reveal = () => {
    onToggle();
    if (handleClicks) handleClicks(entry);
  };

  const handleToggle = () => {
    // Collapsing is unconditional: the notice gates revealing a value,
    // not hiding one.
    if (isOpen) {
      onToggle();
      return;
    }

    // Visitors get the verification notice before the first payment
    // detail on this profile is revealed -- the moment that decides
    // whether someone checks the account title, rather than after they
    // have already copied a number. Owners are exempt (isPublic is false
    // on their own dashboard): it is their own account, and they have
    // already seen the editor's notice.
    //
    // Not shown on every tap. A payer comparing two methods would meet it
    // twice within seconds, and the second showing carries no new
    // information -- it just teaches the reflex of dismissing without
    // reading, which is the opposite of what it is for.
    if (isPublic && !hasSeenNotice(profileKey)) {
      setNoticeOpen(true);
      return;
    }

    reveal();
  };

  // The row expands only after acknowledgement, and the tap is counted
  // there rather than when the notice opened: someone who backs out at
  // the notice never saw the value, so counting it would inflate the
  // owner's figure with reveals that did not happen.
  const acknowledgeNotice = () => {
    rememberNoticeSeen(profileKey);
    setNoticeOpen(false);
    reveal();
  };

  const handleCopy = async () => {
    const ok = await copyText(entry.value);
    setCopied(ok);
    setCopyFailed(!ok);
    if (ok) setTimeout(() => setCopied(false), 2000);
  };

  return (
    <React.Fragment>
      <li className="col-12 payment-item">
        <button
          type="button"
          className="payment-item__row"
          onClick={handleToggle}
          aria-expanded={isOpen}
        >
          <PlatformIcon platform={entry.platform} size={40} src={bank ? bank.icon : null} />
          <div className="payment-item__labels">
            {/* Label first and bold, bank name after it and muted: the
                label is the user's own text and is what tells two IBANs
                apart, while the bank is derived and secondary. The
                separator lives inside the bank span so it cannot be left
                stranded when there is no bank to name. */}
            <p className="payment-item__name" style={{ fontSize: size }}>
              <b>{displayName}</b>
              {bank && (
                <span className="payment-item__bank">
                  {' '}
                  &middot;&nbsp;{bank.name}
                </span>
              )}
            </p>
            {subtitle && <p className="payment-item__subtitle">{subtitle}</p>}
            {/* The same counter the link rows show, from the same field.
                update_clicks increments socialEntries.$.clicks for every
                entry by _id with no kind check, so payment reveals have
                always been counted here -- the row just never rendered
                it, which made an owner comparing rows read a real count
                of zero as "nothing is tracked".

                Deliberately worded "Clicks" like every other row rather
                than "taps": this column exists to be scanned down, and
                one row labelled differently reads as a bug rather than a
                distinction. The payment/click split that matters is the
                Event type behind the analytics screen, which is
                untouched -- see the payment_tap comment in
                route/api/users.js. Owner-only, same as the link rows. */}
            {!isPublic && (
              <p className="payment-item__clicks">
                <span>{entry.clicks || 0}</span> Clicks
              </p>
            )}
          </div>
        </button>

        {isOpen && (
          <div className="payment-item__panel">
            {qrPayload && (
              <div className="payment-item__qr">
                {/* Level M is what payment QRs conventionally use: enough
                    redundancy to survive a phone screen's glare without
                    inflating the module count. */}
                <QRCode value={qrPayload} size={180} level="M" includeMargin={true} />
                <p className="payment-item__qr-caption">
                  Scan with any Pakistani banking app
                </p>
              </div>
            )}

            {/* Always rendered, including under the QR -- the fallback
                that matters when a scan fails, a screen is too dim, or
                the payer is on a desktop with no camera. */}
            <p className="payment-item__value">{entry.value}</p>

            <button type="button" className="payment-item__copy" onClick={handleCopy}>
              {copied ? <FiCheck /> : <FiCopy />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>

            {copyFailed && (
              <p className="payment-item__copy-failed">
                Couldn't copy automatically -- select the value above to copy it.
              </p>
            )}
          </div>
        )}
      </li>
      <span className="payment-item__divider" />

      {noticeOpen && (
        <PaymentNoticeModal
          ariaLabel="About these payment details"
          buttonLabel="Got it"
          onAcknowledge={acknowledgeNotice}
        >
          Digital Contact Card doesn't verify these payment details. Check the account
          title in your banking app before sending.
        </PaymentNoticeModal>
      )}
    </React.Fragment>
  );
}
